import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';
import { useFinanceFilters, FinanceFilterBar } from '../lib/FinanceFilters.js';

interface SkuRow {
  sku: string; revenue: number; unitsSold: number;
  referralFee: number; fbaFee: number; storageFee: number; otherFees: number;
  adSpend: number; skuAdSpend: number; refundAmount: number; cogs: number;
  netProfit: number; cm1: number; cm2: number;
  cm1Pct: number | null; cm2Pct: number | null; breakEvenAcos: number | null;
  marginPct: number | null; tacos: number | null; hasCogs: boolean;
}

interface Account { id: string; name: string; is_active?: number }

type SortField = 'revenue' | 'net_profit' | 'margin_pct' | 'units_sold' | 'cm1' | 'cm2' | 'break_even_acos';

export function FinanceSKU() {
  const [skus, setSkus] = useState<SkuRow[]>([]);
  const [sortBy, setSortBy] = useState<SortField>('cm2');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const { displayName, parentNickname } = useNicknames();
  const filters = useFinanceFilters('30');

  useEffect(() => {
    (async () => {
      if (!loading) setRefreshing(true);
      setError('');
      try {
        const accts = await api.get<Account[]>('/accounts');
        const active = accts.filter(a => a.is_active !== 0);
        if (active.length === 0) { setLoading(false); return; }

        const startDate = new Date(Date.now() - Number(filters.period) * 86400000).toISOString().slice(0, 10);
        const endDate = new Date().toISOString().slice(0, 10);
        const mktParam = filters.marketplace ? `&marketplace=${filters.marketplace}` : '';

        const results = await Promise.all(
          active.map(a => api.get<{ skus: SkuRow[]; marketplaces?: string[] }>(
            `/finance/sku-profitability/${a.id}?startDate=${startDate}&endDate=${endDate}&sortBy=${sortBy}&sortDir=${sortDir}&limit=100${mktParam}${filters.currencyParam}`,
          ).catch(() => ({ skus: [], marketplaces: [] }))),
        );

        // Collect available marketplaces
        const mktSet = new Set<string>();
        for (const r of results) {
          for (const m of (r.marketplaces ?? [])) mktSet.add(m);
        }
        filters.setMarkets([...mktSet].sort());

        // Merge by SKU across accounts
        const merged = new Map<string, SkuRow>();
        for (const r of results) {
          for (const s of r.skus) {
            const ex = merged.get(s.sku);
            if (ex) {
              ex.revenue += s.revenue; ex.unitsSold += s.unitsSold;
              ex.referralFee += s.referralFee; ex.fbaFee += s.fbaFee;
              ex.storageFee += s.storageFee; ex.otherFees += s.otherFees;
              ex.adSpend += s.adSpend; ex.skuAdSpend += s.skuAdSpend;
              ex.refundAmount += s.refundAmount; ex.cogs += s.cogs;
              ex.netProfit += s.netProfit; ex.cm1 += s.cm1; ex.cm2 += s.cm2;
              ex.cm1Pct = ex.revenue > 0 ? (ex.cm1 / ex.revenue) * 100 : null;
              ex.cm2Pct = ex.revenue > 0 ? (ex.cm2 / ex.revenue) * 100 : null;
              ex.breakEvenAcos = ex.cm1Pct && ex.cm1Pct > 0 ? ex.cm1Pct : null;
              ex.marginPct = ex.revenue > 0 ? (ex.netProfit / ex.revenue) * 100 : null;
              ex.hasCogs = ex.hasCogs || s.hasCogs;
            } else {
              merged.set(s.sku, { ...s });
            }
          }
        }

        const list = [...merged.values()];
        list.sort((a, b) => {
          const get = (s: SkuRow): number => {
            switch (sortBy) {
              case 'revenue': return s.revenue;
              case 'net_profit': return s.netProfit;
              case 'margin_pct': return s.marginPct ?? -999;
              case 'units_sold': return s.unitsSold;
              case 'cm1': return s.cm1;
              case 'cm2': return s.cm2;
              case 'break_even_acos': return s.breakEvenAcos ?? -999;
              default: return 0;
            }
          };
          return sortDir === 'desc' ? get(b) - get(a) : get(a) - get(b);
        });
        setSkus(list);
      } catch (e) { setError(String(e)); }
      setLoading(false); setRefreshing(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, sortBy, sortDir, filters.marketplace, filters.currency]);

  function handleSort(field: SortField) {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  }

  function SortTh({ field, label }: { field: SortField; label: string }) {
    return (
      <th onClick={() => handleSort(field)}
        className="pb-2 text-right text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700 select-none whitespace-nowrap">
        {label}{sortBy === field ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
      </th>
    );
  }

  // Group by parent nickname so children of the same parent stay together
  const grouped = useMemo(() => {
    const groups = new Map<string, SkuRow[]>();
    for (const s of skus) {
      const key = parentNickname(s.sku) ?? s.sku;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }
    // Sort groups by the best-performing child in each group (using current sort)
    const getSortVal = (s: SkuRow): number => {
      switch (sortBy) {
        case 'revenue': return s.revenue;
        case 'net_profit': return s.netProfit;
        case 'margin_pct': return s.marginPct ?? -999;
        case 'units_sold': return s.unitsSold;
        case 'cm1': return s.cm1;
        case 'cm2': return s.cm2;
        case 'break_even_acos': return s.breakEvenAcos ?? -999;
        default: return 0;
      }
    };
    const entries = [...groups.entries()];
    entries.sort((a, b) => {
      const bestA = Math.max(...a[1].map(getSortVal));
      const bestB = Math.max(...b[1].map(getSortVal));
      return sortDir === 'desc' ? bestB - bestA : bestA - bestB;
    });
    return entries.flatMap(([, items]) => items);
  }, [skus, parentNickname, sortBy, sortDir]);

  const missingCogs = grouped.filter(s => !s.hasCogs && s.unitsSold > 0).length;

  if (loading) return <div className="py-12 text-center text-gray-400 animate-pulse">Loading SKU profitability...</div>;

  return (
    <div className={`space-y-6 ${refreshing ? 'opacity-60' : ''}`}>
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SKU Profitability <span className="text-xs font-normal text-gray-400 ml-2">All amounts in {filters.currencyLabel}</span></h1>
            <p className="mt-1 text-sm text-gray-500">Per-SKU profit ranking with CM1, CM2, and break-even ACoS</p>
          </div>
        </div>
        <FinanceFilterBar filters={filters} />
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      {missingCogs > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 flex items-center justify-between">
          <p className="text-sm text-yellow-700">{missingCogs} SKU{missingCogs > 1 ? 's' : ''} missing COGS — CM and profit may be overstated</p>
          <a href="/inventory/batches" className="text-xs font-medium text-yellow-800 hover:underline">Add COGS</a>
        </div>
      )}

      {grouped.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm font-medium text-gray-900">No SKU data</p>
          <p className="mt-1 text-xs text-gray-500">Run a financial backfill from the Finance Overview page.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-3 pb-2 text-left text-xs font-medium text-gray-500">SKU</th>
                <SortTh field="revenue" label="Revenue" />
                <SortTh field="units_sold" label="Units" />
                <th className="pb-2 text-right text-xs font-medium text-gray-500">COGS</th>
                <th className="pb-2 text-right text-xs font-medium text-gray-500">Fees</th>
                <SortTh field="cm1" label="CM1" />
                <th className="pb-2 text-right text-xs font-medium text-gray-500">CM1%</th>
                <th className="pb-2 text-right text-xs font-medium text-gray-500">Ad Spend</th>
                <SortTh field="cm2" label="CM2" />
                <th className="pb-2 text-right text-xs font-medium text-gray-500">CM2%</th>
                <SortTh field="break_even_acos" label="BE ACoS" />
                <SortTh field="net_profit" label="Net Profit" />
                <SortTh field="margin_pct" label="Margin" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {grouped.map(s => {
                const totalFees = s.referralFee + s.fbaFee + s.storageFee + s.otherFees;
                return (
                  <tr key={s.sku} className={`hover:bg-gray-50 ${!s.hasCogs && s.unitsSold > 0 ? 'bg-yellow-50/30' : ''}`}>
                    <td className="px-3 py-2 max-w-[160px] font-medium text-gray-900" title={s.sku}>
                      {parentNickname(s.sku) && <span className="text-[10px] text-gray-400 block truncate">{parentNickname(s.sku)}</span>}
                      <span className="truncate block">{displayName(s.sku)}{!s.hasCogs && s.unitsSold > 0 && <span className="ml-1 text-yellow-500" title="Missing COGS">*</span>}</span>
                    </td>
                    <td className="py-2 text-right text-gray-700">{filters.fmt(s.revenue)}</td>
                    <td className="py-2 text-right text-gray-500">{s.unitsSold}</td>
                    <td className="py-2 text-right text-gray-500">{s.cogs > 0 ? filters.fmt(s.cogs) : <span className="text-gray-300">--</span>}</td>
                    <td className="py-2 text-right text-gray-500">{filters.fmt(totalFees)}</td>
                    <td className={`py-2 text-right font-medium ${s.cm1 >= 0 ? 'text-green-600' : 'text-red-600'}`}>{filters.fmt(s.cm1)}</td>
                    <td className={`py-2 text-right text-xs ${cmColor(s.cm1Pct, 35, 20)}`}>
                      {s.cm1Pct !== null ? `${s.cm1Pct.toFixed(1)}%` : '--'}
                    </td>
                    <td className="py-2 text-right text-gray-500">{filters.fmt(s.skuAdSpend || s.adSpend)}</td>
                    <td className={`py-2 text-right font-medium ${s.cm2 >= 0 ? 'text-green-600' : 'text-red-600'}`}>{filters.fmt(s.cm2)}</td>
                    <td className={`py-2 text-right text-xs ${cmColor(s.cm2Pct, 15, 0)}`}>
                      {s.cm2Pct !== null ? `${s.cm2Pct.toFixed(1)}%` : '--'}
                    </td>
                    <td className="py-2 text-right text-xs font-medium text-blue-600">
                      {s.breakEvenAcos !== null ? `${s.breakEvenAcos.toFixed(0)}%` : '--'}
                    </td>
                    <td className={`py-2 text-right font-medium ${s.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{filters.fmt(s.netProfit)}</td>
                    <td className={`py-2 text-right text-xs font-medium ${cmColor(s.marginPct, 15, 0)}`}>
                      {s.marginPct !== null ? `${s.marginPct.toFixed(1)}%` : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {grouped.length > 1 && (() => {
              const t = grouped.reduce((acc, s) => {
                acc.revenue += s.revenue; acc.units += s.unitsSold; acc.cogs += s.cogs;
                acc.fees += s.referralFee + s.fbaFee + s.storageFee + s.otherFees;
                acc.cm1 += s.cm1; acc.cm2 += s.cm2;
                acc.adSpend += s.skuAdSpend || s.adSpend;
                acc.netProfit += s.netProfit;
                return acc;
              }, { revenue: 0, units: 0, cogs: 0, fees: 0, cm1: 0, cm2: 0, adSpend: 0, netProfit: 0 });
              const cm1Pct = t.revenue > 0 ? (t.cm1 / t.revenue) * 100 : null;
              const cm2Pct = t.revenue > 0 ? (t.cm2 / t.revenue) * 100 : null;
              const marginPct = t.revenue > 0 ? (t.netProfit / t.revenue) * 100 : null;
              const beAcos = cm1Pct !== null && cm1Pct > 0 ? cm1Pct : null;
              return (
                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                  <tr className="font-semibold">
                    <td className="px-3 py-2 text-gray-900">Total</td>
                    <td className="py-2 text-right text-gray-900">{filters.fmt(t.revenue)}</td>
                    <td className="py-2 text-right text-gray-700">{t.units}</td>
                    <td className="py-2 text-right text-gray-700">{t.cogs > 0 ? filters.fmt(t.cogs) : '--'}</td>
                    <td className="py-2 text-right text-gray-700">{filters.fmt(t.fees)}</td>
                    <td className={`py-2 text-right ${t.cm1 >= 0 ? 'text-green-700' : 'text-red-700'}`}>{filters.fmt(t.cm1)}</td>
                    <td className={`py-2 text-right text-xs ${cmColor(cm1Pct, 35, 20)}`}>
                      {cm1Pct !== null ? `${cm1Pct.toFixed(1)}%` : '--'}
                    </td>
                    <td className="py-2 text-right text-gray-700">{filters.fmt(t.adSpend)}</td>
                    <td className={`py-2 text-right ${t.cm2 >= 0 ? 'text-green-700' : 'text-red-700'}`}>{filters.fmt(t.cm2)}</td>
                    <td className={`py-2 text-right text-xs ${cmColor(cm2Pct, 15, 0)}`}>
                      {cm2Pct !== null ? `${cm2Pct.toFixed(1)}%` : '--'}
                    </td>
                    <td className="py-2 text-right text-xs font-medium text-blue-600">
                      {beAcos !== null ? `${beAcos.toFixed(0)}%` : '--'}
                    </td>
                    <td className={`py-2 text-right ${t.netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{filters.fmt(t.netProfit)}</td>
                    <td className={`py-2 text-right text-xs ${cmColor(marginPct, 15, 0)}`}>
                      {marginPct !== null ? `${marginPct.toFixed(1)}%` : '--'}
                    </td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      )}
    </div>
  );
}

function cmColor(pct: number | null, good: number, ok: number): string {
  if (pct === null) return 'text-gray-400';
  return pct >= good ? 'text-green-600' : pct >= ok ? 'text-yellow-600' : 'text-red-600';
}
